// src/pages/DebtStudio/components/Issues/DealStructure/hooks/useLinkedFund.js
import { useEffect, useMemo, useState } from "react";
import { supabase } from "services/supabaseClient";

function safeJson(value, fallback) {
  if (value == null) return fallback;
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return fallback;
    }
  }
  return value;
}

function num(x, dflt = null) {
  const n = Number(x);
  return Number.isFinite(n) ? n : dflt;
}

/**
 * Extract FundStructurer Total Amount from fp_funds.form (canonical).
 * Supports both stored JSON object and stringified JSON.
 */
function extractFundTotalAmount(fundRow) {
  const form = safeJson(fundRow?.form, null);
  const v = form?.totalAmount ?? form?.total_amount ?? null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Compute Total Portfolio Cost from fp_funds.solution
 * Be tolerant to row schema changes:
 * - prefer explicit cost fields (totalCost, purchaseCost, cost)
 * - fallback to principal * price/100 when available
 */
function computeTotalPortfolioCostFromFundRow(fundRow) {
  try {
    const sol = safeJson(fundRow?.solution, null);
    const investments = Array.isArray(sol?.investments) ? sol.investments : [];
    if (!investments.length) return null;

    let total = 0;
    let any = false;

    for (const r of investments) {
      if (!r || typeof r !== "object") continue;

      const explicit =
        num(r?.totalCost) ??
        num(r?.purchaseCost) ??
        num(r?.cost) ??
        num(r?.amount) ??
        null;

      if (explicit != null) {
        total += explicit;
        any = true;
        continue;
      }

      const principal =
        num(r?.principal) ?? num(r?.par) ?? num(r?.face) ?? num(r?.notional) ?? null;
      const price = num(r?.price) ?? null;

      if (principal != null && price != null) {
        total += principal * (price / 100);
        any = true;
      }
    }

    return any && Number.isFinite(total) && total > 0 ? total : null;
  } catch {
    return null;
  }
}

/**
 * useLinkedFund
 * Looks up the linked FundPlanner fund for a DebtStudio deal.
 * fundType defaults to "project" (new money), refunding deals pass "refunding".
 */
export function useLinkedFund({ orgId, dealId, fundType = "project" }) {
  const [loading, setLoading] = useState(false);
  const [fundId, setFundId] = useState(null);
  const [fundTotalAmount, setFundTotalAmount] = useState(null);
  const [totalPortfolioCost, setTotalPortfolioCost] = useState(null);
  const [error, setError] = useState(null);

  const canonicalFundType = useMemo(() => {
    const s = String(fundType || "").trim().toLowerCase();
    return s === "refunding" ? "refunding" : "project";
  }, [fundType]);

  useEffect(() => {
    let cancelled = false;

    // Only meaningful when deal is saved and org is known
    if (!orgId || !dealId) {
      setFundId(null);
      setFundTotalAmount(null);
      setTotalPortfolioCost(null);
      setError(null);
      setLoading(false);
      return;
    }

    const run = async () => {
      setLoading(true);
      setError(null);

      try {
        const { data, error: qErr } = await supabase
          .from("fp_funds")
          .select("id, solution, form, fund_type")
          .eq("org_id", orgId)
          .eq("linked_ds_deal_id", dealId)
          .eq("fund_type", canonicalFundType)
          .maybeSingle();

        if (cancelled) return;
        if (qErr) throw qErr;

        if (!data?.id) {
          setFundId(null);
          setFundTotalAmount(null);
          setTotalPortfolioCost(null);
          return;
        }

        setFundId(data.id);
        setFundTotalAmount(extractFundTotalAmount(data));
        setTotalPortfolioCost(computeTotalPortfolioCostFromFundRow(data));
      } catch (e) {
        if (cancelled) return;
        console.error("❌ [useLinkedFund] lookup failed:", e);
        setFundId(null);
        setFundTotalAmount(null);
        setTotalPortfolioCost(null);
        setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [orgId, dealId, canonicalFundType]);

  return {
    loading,
    error,
    fundId,
    fundTotalAmount,
    totalPortfolioCost,
  };
}
